import { createRouter, createWebHistory } from 'vue-router';
import { useAuthStore } from '@/store/use-auth-store';
import AppLayout from '@/layouts/app-layout.vue';

const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      path: '/login',
      name: 'login',
      component: () => import('@/views/login/login.vue'),
      meta: { isPublic: true },
    },
    {
      path: '/review/:token/billing',
      name: 'review-billing',
      component: () => import('@/views/review/order-form-billing.vue'),
      meta: { isOpen: true },
    },
    {
      path: '/review/:token',
      name: 'review',
      component: () => import('@/views/review/order-form-review.vue'),
      meta: { isOpen: true },
    },
    {
      path: '/',
      component: AppLayout,
      children: [
        {
          path: '',
          name: 'home',
          component: () => import('@/views/home/home.vue'),
        },
        {
          path: 'rate-card',
          name: 'rate-card',
          component: () => import('@/views/rate-card/rate-card.vue'),
        },
        {
          path: 'rate-card/edit',
          name: 'rate-card-edit',
          component: () => import('@/views/rate-card/rate-card-edit.vue'),
          meta: { requiresRateCardManagement: true },
        },
        {
          path: 'activities',
          name: 'activities',
          component: () => import('@/views/activities/activities.vue'),
        },
        {
          path: 'commissions',
          name: 'commissions',
          component: () => import('@/views/commissions/commissions.vue'),
        },
        {
          path: 'financials',
          name: 'financials',
          component: () => import('@/views/financials/financials.vue'),
          meta: { requiresFinancials: true },
        },
        {
          path: 'approvals',
          name: 'approvals',
          component: () => import('@/views/approvals/approvals.vue'),
          meta: { requiresApprovalPermission: true },
        },
        {
          path: 'quotes',
          name: 'quotes',
          component: () => import('@/views/quotes/quotes-list.vue'),
        },
        {
          path: 'quotes/new',
          name: 'quote-new',
          component: () => import('@/views/quotes/quote-editor.vue'),
        },
        {
          path: 'quotes/:id',
          name: 'quote',
          component: () => import('@/views/quotes/quote-editor.vue'),
        },
        {
          path: 'order-forms',
          name: 'order-forms',
          component: () => import('@/views/order-forms/order-forms-list.vue'),
        },
        {
          path: 'order-forms/new',
          name: 'order-form-new',
          component: () => import('@/views/order-forms/order-form-editor.vue'),
        },
        {
          path: 'order-forms/:id',
          name: 'order-form',
          component: () => import('@/views/order-forms/order-form-detail.vue'),
        },
        {
          path: 'order-forms/:id/edit',
          name: 'order-form-edit',
          component: () => import('@/views/order-forms/order-form-editor.vue'),
        },
        {
          path: 'order-form-template',
          name: 'order-form-template',
          component: () =>
            import('@/views/order-form-template/order-form-template.vue'),
          meta: { requiresTemplateManagement: true },
        },
        {
          path: 'users',
          name: 'users',
          component: () => import('@/views/users/users.vue'),
          meta: { requiresUserManagement: true },
        },
      ],
    },
    { path: '/:pathMatch(.*)*', redirect: '/' },
  ],
});

router.beforeEach(async (to) => {
  if (to.meta.isOpen) {
    return true;
  }

  const authStore = useAuthStore();
  await authStore.initialize();

  if (to.meta.isPublic) {
    return authStore.user ? { name: 'home' } : true;
  }
  if (!authStore.user) {
    return { name: 'login' };
  }
  if (to.meta.requiresUserManagement && !authStore.canManageUsers) {
    return { name: 'home' };
  }
  if (to.meta.requiresApprovalPermission && !authStore.canApproveDiscounts) {
    return { name: 'home' };
  }
  if (to.meta.requiresFinancials && !authStore.canViewFinancials) {
    return { name: 'home' };
  }
  if (to.meta.requiresTemplateManagement && !authStore.canManageTemplates) {
    return { name: 'home' };
  }
  if (to.meta.requiresRateCardManagement && !authStore.canManageRateCard) {
    return { name: 'rate-card' };
  }
  return true;
});

export default router;
